import { lazy, useRef, useState } from "react";
import {
  BlockWrapper,
  ChildrenBlock,
  defineBlock,
  smartProps,
  useChildrenBlocks,
  useInspectorControl,
} from "@smartfire/js-block-engine";
import { Swiper, SwiperSlide } from "swiper/react";
import { ReactComponent as ArrowCircle } from "../assets/icons/arrow-circle.svg";
import useMediaQuery from "@mui/material/useMediaQuery";
import clsx from "clsx";
import "swiper/css";
import style from "./block.module.scss";

const InspectorLazy = lazy(() => import("./Inspector"));

function Block({
  bevel,
  marginNeg,
  position,
  background,
  columns,
  swiperMobile,
  swiperMobileSlides,
}) {
  useInspectorControl(InspectorLazy, []);
  const { children, Child } = useChildrenBlocks();
  const isMobile = useMediaQuery("(max-width: " + style.mobileBreakpoint + ")");
  const swiperRef = useRef(null);
  const [firstSlide, setFirstSlide] = useState(true);
  const [lastSlide, setLastSlide] = useState(false);

  const goPrev = () => {
    if (swiperRef.current) {
      swiperRef.current?.slideTo(swiperRef.current?.activeIndex - 1);
    }
  };

  const goNext = () => {
    if (swiperRef.current) {
      swiperRef.current?.slideTo(swiperRef.current?.activeIndex + 1);
    }
  };

  const handleSlideChange = (swiper) => {
    setFirstSlide(swiper.isBeginning);
    setLastSlide(swiper.isEnd);
  };

  return (
    <BlockWrapper.Div
      className={clsx(
        style.block,
        bevel && style.bevel,
        !isMobile && marginNeg && style.marginNeg,
        style[position]
      )}
      style={{
        "--number-key-background": background,
        "--columns": columns[0],
        "--columns-tablet": columns[1],
        "--columns-mobile": columns[2],
      }}
    >
      {isMobile && swiperMobile ? (
        <div className={style.swiperContainer}>
          <Swiper
            slidesPerView={swiperMobileSlides}
            spaceBetween={10}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            onSlideChange={handleSlideChange}
          >
            {children &&
              children.length > 0 &&
              children.map((_, i) => {
                return (
                  <SwiperSlide key={i}>
                    <Child index={i} />
                  </SwiperSlide>
                );
              })}
          </Swiper>

          <div className={style.swiperNav}>
            <ArrowCircle
              className={clsx(firstSlide && style.disabled)}
              onClick={goPrev}
            />
            <ArrowCircle
              className={clsx(lastSlide && style.disabled)}
              onClick={goNext}
            />
          </div>
        </div>
      ) : (
        <ChildrenBlock
          gutenbergEditorConfig={{
            allowedBlocks: ["smartfire/number-key---item"],
          }}
          className={clsx(
            style.childrenBlocks,
            !swiperMobile && style.noSwiperMobile
          )}
        />
      )}
    </BlockWrapper.Div>
  );
}

export default defineBlock({
  Block,
  smartProps: {
    bevel: smartProps.bool.withDefault(false),
    marginNeg: smartProps.bool.withDefault(false),
    position: smartProps.string.withDefault("centered"),
    background: smartProps.string.withDefault(""),
    columns: smartProps.array.withDefault([3, 2, 1]),
    swiperMobile: smartProps.bool.withDefault(true),
    swiperMobileSlides: smartProps.number.withDefault(2),
  },
  setup: {
    title: "Number key",
  },
});
